import { towersThatHit, weakTo } from "@highmarch/app";
import type { BalanceData, Enemy } from "@highmarch/domain";
import { ROLE_LABEL, enemyLook } from "../render/views/enemyLook";
import { badgeDataUrl } from "../render/views/enemyGlyphs";
import { icbtnHtml } from "./kit";
import { icon } from "./iconsKit";
import { portraitImg, portraitUrl } from "./portraits";
import { enemyActLabel, enemyTraitLines } from "./enemyTraits";
import { IMMUNE_STATUS, STATUS_COLOUR } from "../render/views/enemyLook";
import { STATUS_CELL, glyphDataUrl } from "../render/views/enemyGlyphs";
import { immunityKinds, immunityLine, immunityOf, isRareArchetype, rareById } from "../rare/rareApi";
import { isWyrmArchetype } from "../rare/wyrmApi";
import { WYRM_COPY } from "./wyrmModel";
import "./styles/wyrm.css";

/**
 * The enemy info card (QoL R1): tap an enemy (or long-press it), or a chip in the wave strip, for what it is, how it
 * stands, and what it is weak to (the shared table in app/counters.ts). A card is advice about the enemy type; for a
 * live enemy it adds its health and statuses. It flags an enemy none of the player's towers can hit.
 */
export interface EnemyCardFacts {
  readonly archetype: number;
  readonly boss: boolean;
  /** The enemy on the map, when the card is for one (its health and statuses); null for a wave chip. */
  readonly live: Enemy | null;
  /** Which towers the player owns, by archetype index (the pool this level draws from). */
  readonly owned: readonly boolean[];
  /** M36: the immunity of the wave chip tapped (a live enemy's own is read from it). */
  readonly immunity?: number;
  /** M36: the enemy is the rare spawn (a live one; a rare archetype says so itself). */
  readonly rare?: boolean;
}

/** M36: what an immunity means to the player, after "Immune to …". */
const IMMUNE_ADVICE: Readonly<Record<"physical" | "magic" | "fire" | "slow", string>> = {
  physical: "arrows, bolts and shot do nothing — use magic or fire",
  magic: "spells and beams do nothing — use arrows, cannons or fire",
  fire: "flames and burns do nothing — use another tower",
  slow: "frost and slows do nothing — kill it before it runs through",
};

/** The card's M36 lines: the immunity (glyph, words, what to do) and the rare's stakes. */
function m36Lines(balance: BalanceData, f: EnemyCardFacts): string {
  const imm = f.live ? immunityOf(f.live) : f.immunity ?? 0;
  let out = "";
  if (imm) {
    const kinds = immunityKinds(imm);
    const icons = kinds.map((k) => `<img src="${glyphDataUrl(STATUS_CELL[IMMUNE_STATUS[k]], STATUS_COLOUR[IMMUNE_STATUS[k]], 40)}" alt="">`).join("");
    out += `<div class="imm-line">${icons}<span>${immunityLine(imm)}<br><small>${kinds.map((k) => IMMUNE_ADVICE[k]).join("; ")}</small></span></div>`;
  }
  const a = balance.enemies[f.archetype];
  if (f.rare || isRareArchetype(balance, f.archetype)) {
    const name = rareById(a.id)?.name ?? a.name;
    out += `<div class="rare-line">★ RARE · ${name} — slay it for a rare chest. If it reaches the gate, the battle is lost</div>`;
  }
  // M40: the Starfall Wyrm's stakes
  if (isWyrmArchetype(balance, f.archetype)) out += `<div class="legend-line">${WYRM_COPY.cardLine}</div>`;
  return out;
}

/** An enemy's pace in words, against the Grunt's 1 square a second (the balance's speeds run 0.6 to 2). */
export function paceWord(cellsPerSecond: number): string {
  if (cellsPerSecond < 0.8) return "slow";
  if (cellsPerSecond < 0.95) return "a little slow";
  if (cellsPerSecond <= 1.1) return "steady pace";
  if (cellsPerSecond < 1.6) return "fast";
  return "very fast";
}

/** Tower portrait size on the card's "weak to" row. */
const WEAK_PORTRAIT_PX = 26;

export function enemyCardHtml(balance: BalanceData, f: EnemyCardFacts): string {
  const a = balance.enemies[f.archetype], look = enemyLook(a, f.boss);
  const pic = portraitUrl(a.id);
  const face = pic ? `<img class="face" decoding="sync" src="${pic}" alt="">` : `<img class="face badge" src="${badgeDataUrl(look.role, look.colour)}" alt="">`;
  const armour = a.armour.isPositive() ? ` · armour ${Math.round(a.armour.toNumber() * 100)} %` : "";
  // a rare wears the boss look: "Rare", not the boss role singularised to "Bos"
  const role = isWyrmArchetype(balance, f.archetype) ? "Legendary" : f.rare || isRareArchetype(balance, f.archetype) ? "Rare" : f.boss || look.role === "boss" ? "Boss" : ROLE_LABEL[look.role].replace(/s$/, "");
  // the role only when it says something the name doesn't ("Shieldbearer · Shieldbearer" read as a glitch), and the
  // pace in words against a Grunt's (a bare "speed 0.8" had no unit a player could read)
  const roleWord = `${role.charAt(0).toUpperCase()}${role.slice(1)}`;
  const sub = [roleWord.toLowerCase() === a.name.toLowerCase() ? "" : roleWord, a.isAir ? "flies" : "walks", armour.replace(/^ · /, ""), paceWord(a.speedCellsPerSecond.toNumber())].filter(Boolean).join(" · ");
  const e = f.live;
  const status = e ? [enemyActLabel(e), e.slowTicksLeft > 0 ? "slowed" : "", e.burnTicksLeft > 0 ? "burning" : "", e.shredStacks > 0 ? `shred ×${e.shredStacks}` : "", e.boostTicksLeft > 0 ? "boosted" : ""].filter(Boolean).join(" · ") : "";
  const traits = enemyTraitLines(balance, a);
  const hp = e ? `<div class="hp">HP <b>${Math.ceil(e.hp.toNumber())}</b>/${Math.ceil(e.maxHp.toNumber())}${status ? ` · ${status}` : ""}</div>` : "";
  const counter = weakTo(balance, a.id);
  const towers = counter.towers.map((t) => `<span class="tw${f.owned[t.index] ? "" : " off"}">${portraitImg(t.id, WEAK_PORTRAIT_PX, icon("tower", 18, "#fff8e6"))}<i>${t.name}</i></span>`).join("");
  const canHit = towersThatHit(balance, a).some((t) => f.owned[t.index]);
  const warn = canHit ? "" : `<div class="warn">${icon("skull", 14)} None of your towers can hit it${a.isAir ? " — it flies" : ""}</div>`;
  return `<div class="head">${face}<div class="t"><b class="disp">${a.name}</b><small>${sub}</small></div>${icbtnHtml("close", "close", "", null, 36)}</div>${hp}${m36Lines(balance, f)}`
    + (traits.length ? `<div class="why">${traits.join(". ")}.</div>` : "")
    + (towers ? `<div class="weak"><small class="cap">WEAK TO</small>${towers}</div><div class="why">${counter.why}</div>` : "") + warn;
}

/** How long the card stays up untouched (ms): long enough to read the line and the three towers. */
export const ENEMY_CARD_MS = 6000;

/** The card on screen: one at a time, closed by its × , a tap on it, a timer, or the level. */
export class EnemyCard {
  private el: HTMLElement | null = null;
  private timer = 0;

  constructor(private readonly parent: HTMLElement) {}

  get shown(): boolean { return this.el !== null; }

  show(balance: BalanceData, f: EnemyCardFacts): void {
    this.close();
    const el = document.createElement("div");
    el.className = "enemy-card plate";
    el.setAttribute("role", "dialog");
    el.innerHTML = enemyCardHtml(balance, f);
    el.addEventListener("click", () => this.close());
    this.parent.append(el);
    this.el = el;
    this.timer = window.setTimeout(() => this.close(), ENEMY_CARD_MS);
  }

  close(): void {
    window.clearTimeout(this.timer);
    this.el?.remove();
    this.el = null;
  }
}
